import { Dispatch, DragEvent, SetStateAction, SyntheticEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import styles from "./FloatingSideSheet.module.css";

import Icon from "../../common/Icon";
import Tabs from "../../common/Tabs";
import { Field, ImageInfo, MapInfo, PlaceData } from "../../common/Common";
import MainContentItem from "../../common/MainContentItem";
import { ToastData, ToastType } from "../../common/Toast";
import ButtonClose from "./ButtonClose";
import MorePictures from "./MorePictures";
import ThumbButton from "./ThumbButton";
import DelConfirm from "./DelConfirm";
import ContentTitle from "../sidesheet/ContentTitle";
import StoryView from "./StoryView";
import GridGallery from "./GridGallery";
import { Content, defaultStyle, MainImageType } from "../../createmap/CreateMap";
import { ContentList } from "../../../screens/MapScreen";
import { DataTemplate } from "../sidesheet/addplace/AddSinglePlaceInput";
import DataProvider from "../../../data_provider/DataProvider";
import { MiniButtonType } from "../MiniButton";
import BottomButtonBox from "../BottomButtonBox";

import ModalButtons from "../../modal/ModalButtons";
import SideSheetHandle from "../sidesheet/SideSheetHandle";

import LoginForm from "../../login/LoginForm";
import SignUpModal from "../../login/SignUpModal";
import ImageModal from "./ImageModal";
import { MapInfoOptions } from "../../../data_provider/gateways/MapGateway";
import { useLoginStatus } from "../../../hooks/useLoginStatus";
import StoryGridGallery from "./story/StoryGridGallery";
import { AccountManager } from "../../../workers/AccountManager";

const alphabet = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "W", "X", "Y", "Z"];

/**
 * @author smyun
 * @date 2024-08-20
 * @remark 지도 사이드 시트의 추가 플로팅 컴포넌트
 * @param props POIProps
 * @props POI 장소 정보
 * @returns <section>
 */

const enum TabIndex {
    MAIN,
    DETAIL,
    STORY,
    DELETE,
    GALLERY,
    STORYGALLERY,
}

interface PrimaryDataType {
    placeName: string; // 제목
    images?: ImageItem[];
    remainImgCnt?: number; // 남은 이미지 수
    imageUrl?: string; // 대표이미지
    imageCnt?: number; // 이미지 수
    viewCnt: number; // 조회수
    thumbCnt: number; //도움돼요 수
    thumb: boolean; // 도움돼요
}

interface ImageItem {
    iid: number;
    src: string;
    isPrimary: boolean;
}

interface MainDataBundleType {
    // 주요 정보
    [key: string]: Field;
}

interface DetailDataType {
    // 상세 정보
    [key: string]: string;
}

export interface placeInfoType {
    contents: Content[];
    selectedId: number;
    title: string;
}

// 이미지 옵션
export interface ImageOptions {
    url: string;
    IID: number;
    first?: boolean;
    last?: boolean;
}

interface FloatingSideSheetProps {
    changeSideSheet?: (type: ContentList, reset?: boolean) => void;
    setSelectedPoiIdx?: (value: number) => void;

    selectedPoiIdx?: number | string;
    setToast?: React.Dispatch<React.SetStateAction<ToastData | undefined>>;

    // 신규 장소 미리보기
    newPlace?: PlaceData;
    isAddMode?: boolean;

    // 지도만들기용 props
    mapData?: { row1: string[]; row2: string[] };
    mapSettingData?: {
        placeInfo: placeInfoType;
    };
    setMapSettingData?: Dispatch<
        SetStateAction<{
            placeInfo: placeInfoType;
        }>
    >;

    // 유저 액팅 props | 2025.04.15 | kmy
    thumbState?: boolean;
    setThumbState?: (value: boolean) => void;

    clickedMapId?: string | null;

    // 사이드바 핸들
    showFloatingSheet?: boolean;
    showSideSheet?: boolean;
    shouldSideRender?: boolean;
    shouldFloatingRender?: boolean;
    setShowSideSheet?: Dispatch<SetStateAction<boolean>>;
    setShowFloatingSheet?: Dispatch<SetStateAction<boolean>>;

    pageType?: string;

    onPOIReload?: () => void;

    setEditPlaceStatus?: Dispatch<SetStateAction<boolean>>;
    prevSelectedIdxRef?: React.MutableRefObject<number | null>;

    isMobileView?: boolean; // 모바일 뷰 여부

    mainImage?: MainImageType;

    openMapScreenModal?: (modalProps: any) => void; // 지도 화면 모달 열기 함수
    closeMapScreenModal?: () => void; // 지도 화면 모달 닫기 함수

    isAnalysisActive?: boolean; // 25.07.11 | Kim Young Jun | 분석 도구 활성화 여부
    
    onClearSelectedMarker?: () => void; // 선택된 마커 해제를 위한 콜백

    // 25.07.17 | Kim Young Jun | 사이드 시트 닫힘 상태 추가
    isClosedSideSheet?: boolean | undefined;
    setIsClosedSideSheet?: Dispatch<SetStateAction<boolean>> | undefined;

    selectedMapIdx?: number;
}

const FloatingSideSheet = (props: FloatingSideSheetProps) => {
    // 25.07.10 | Kim Young Jun | 모달 시트안에 갖히는 현상 수정
    const { openMapScreenModal, closeMapScreenModal } = props;

    const navigate = useNavigate();

    const isLogin = useLoginStatus(); // login검증 커스텀 훅 이용 | smyun | 250723
    
    const menu: string[] = ["주요정보", "상세정보", "이야기"];
    const [params] = useSearchParams();
    const [mapid, setMapId] = useState<string>(props.clickedMapId ?? params.get("id") ?? "");

    // 관리자 여부
    const [isAdmin, setIsAdmin] = useState(false);

    // 지도 정보
    const [primaryData, setPrimaryData] = useState<PrimaryDataType>(); // 지도 기본 정보

    // 장소 정보
    const [mainData, setMainData] = useState<MainDataBundleType>();
    const [detailData, setDetailData] = useState<DetailDataType>();

    const [tabMenuIdx, setTabMenuIdx] = useState<TabIndex>(TabIndex.MAIN);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const allowedFileTypes = ["image/png", "image/jpeg", "image/gif"];
    const fileUploadRef = useRef<HTMLInputElement>(null);

    const [isCreator, setIsCreator] = useState<boolean>(false); // 지도 제작자인지 확인
    const [addCheck, setAddCheck] = useState<boolean>(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const [isFullView, setIsFullView] = useState<boolean>(false);
    const [storyImages, setStoryImages] = useState<ImageInfo[] | undefined>(undefined);

    // 지도만들기 관련 ---------------------------------
    const [mapData, mapSettingData, setMapSettingData] = [props.mapData, props.mapSettingData, props.setMapSettingData];
    const isCreateMap = mapData && mapSettingData && setMapSettingData !== undefined;

    const dragItem = useRef<number>(); // 드래그할 아이템의 인덱스
    const dragOverItem = useRef<number>();

    const showModal = (message: string) => {
        openMapScreenModal &&
            openMapScreenModal({
                title: "안내",
                children: message,
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () => closeMapScreenModal && closeMapScreenModal(),
                            },
                        ]}
                    />
                ),
            });
    };

    const showErrorModal = (message: string) => {
        openMapScreenModal &&
            openMapScreenModal({
                title: "오류",
                children: message,
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Danger",
                                onClick: () => closeMapScreenModal && closeMapScreenModal(),
                            },
                        ]}
                    />
                ),
            });
    };

    const MAX_CONTENT_COUNT  = 10;

    // 초기 세팅
    useEffect(() => {
        if (!isCreateMap && !props.isAddMode) {
            getMapInfo(0);
            checkCreator();
        }
    }, []);

    useEffect(() => {
        if (!isCreateMap) {
            reqData();
            checkCreator();
        }
    }, [props.selectedPoiIdx, tabMenuIdx]);

    useEffect(() => {
        getMapInfo(0);
    }, [previewImage]);

    useEffect(() => {
        if (props.clickedMapId != null && props.clickedMapId != undefined) {
            setMapId(props.clickedMapId);
        }
    }, [props.clickedMapId]);

    useEffect(() => {
        if (!isCreateMap) {
            if (props.selectedPoiIdx && mapid) {
                // DataProvider.UpdatePlaceViewCnt({ mapid, XID: props.selectedPoiIdx.toString() }).then((response) => {
                //     // 에러용 모달/팝업 추가(500에러)
                //     if (!response.flag) {
                //         response.status && response.status === 204 && closeFSSheet();
                        
                //     } else {
                //         getMapInfo(0);
                //     }
                // });

                // hmchoi 오류별 처리 구현
                DataProvider.UpdatePlaceViewCnt({ mapid, XID: props.selectedPoiIdx.toString() }).then((response) => {
                    if (response.status === 204) { closeFSSheet(); return; }
                    if (!response.flag) {
                        if (response.status === 500) {
                            showErrorModal("조회수 업데이트를 하다 오류가 발생하였습니다.");
                        }
                    }

                    getMapInfo(0);
                });
            }
        }
         // 관리자 계정인지 확인 
        (async () => {
            const isAdmin = await AccountManager.getInstance().isAdminUser();
            setIsAdmin(isAdmin);
        })();
    }, [props.selectedPoiIdx]);

    useEffect(() => {
        if (isCreateMap) {
            let contentsLen = mapSettingData.placeInfo.contents.length;
            let _mapSettingData = { ...mapSettingData };
            _mapSettingData.placeInfo.selectedId = Number(contentsLen - 1);
            setMapSettingData(_mapSettingData);
        }

        if (contentRef.current) {
            const { scrollHeight, clientHeight } = contentRef.current;
            contentRef.current.scrollTop = scrollHeight - clientHeight;
        }
    }, [addCheck]);

    useEffect(() => {
        const tapMenuElem = document.getElementById("topMenu");
        // tabMenu 변경 시
        if (tapMenuElem) {
            const menuItems = Array.from(tapMenuElem.children);
            menuItems.forEach((elem) => elem.classList.remove(styles.select));
            menuItems[Math.min(2, tabMenuIdx)].classList.add(styles.select);
        }
    }, [tabMenuIdx]);

    useEffect(() => {
        if (props.newPlace) {
            showPreview();
        }
    }, [props.newPlace]);

    const checkGuestMap = async (): Promise<boolean> => {
        const properties: MapInfoOptions[] = ["userId"];
        const responseMapInfo = await DataProvider.GetMapInfo({ mapid }, { properties });

        if (!responseMapInfo.flag || !responseMapInfo.result) {
            return false;
        }
        const result = responseMapInfo.result as MapInfo;
        if (!result.userId) {
            return false;
        }

        return result.userId === "비회원";
    };

    // 장소 제작자 확인
    const checkCreator = async () => {
        if (!props.selectedPoiIdx) return;

        const isGuestMap: boolean = await checkGuestMap();

        if (isLogin) {            
            // 일반 지도
            const response = await DataProvider.GetPlaceOwner({ mapid, XID: props.selectedPoiIdx.toString() });

            if (response.flag) {
                switch (response.code) {
                    case "FORBIDDEN":
                        setIsCreator(false);
                        break;
                    case "POI CREATOR OK": // 장소 추가자
                        setIsCreator(true);
                        break;
                    case "MAP CREATOR OK": // 지도 제작자
                        setIsCreator(true);
                        break;
                    default:
                        setIsCreator(false);
                }
            } else {
                setIsCreator(false);
                console.warn("지도/장소 제작자 조회 실패")
            }
            // 비회원 제작 지도
            if (isGuestMap) {
                setIsCreator(true);
            }
        } else {
            setIsCreator(false);
        }
    };

    const reqData = async () => {
        const isNewData = typeof props.selectedPoiIdx === "string";
        if (!isNewData && tabMenuIdx < 2) await getMapInfo(tabMenuIdx + 1);
    };

    const showPreview = () => {
        if (props.newPlace) {
            const { mainData, extraData, placeName, imageField } = props.newPlace;
            const _primaryData: PrimaryDataType = {
                placeName: placeName.value,
                viewCnt: 0,
                thumbCnt: 0,
                thumb: false,
                imageUrl: imageField ? (imageField.value[0].src as string) : undefined,
                imageCnt: imageField ? 1 : 0,
            };

            setPrimaryData(_primaryData);
            
            if (tabMenuIdx === TabIndex.DETAIL) {
                if (extraData) {
                    const _detail: DetailDataType = {};

                    extraData.forEach((data) => {
                        _detail[data.fieldName] = data.value;
                    });

                    setDetailData(_detail);
                }
            } else {
                const _main: MainDataBundleType = {};

                mainData.forEach((data, idx) => {
                    _main[`data_${idx}`] = data;
                });

                setMainData(_main);
            }
        }
    };
    const changeListItemValue = (data: PrimaryDataType) => {
        const targetItem = document.getElementById(`poi_${props.selectedPoiIdx}`) as Element;
        if (targetItem) {
            const viewCntElem = targetItem.querySelector("#view") as Element;
            if (viewCntElem) viewCntElem.textContent = String(data.viewCnt);
        }
        setTabMenuIdx(TabIndex.MAIN);
    };

    const onClickSignUp = () => {
        openMapScreenModal &&
            openMapScreenModal({
                title: "회원가입",
                children: <SignUpModal onClose={() => closeMapScreenModal && closeMapScreenModal()} />,
                modalButtons: null,
                size: "normal",
            });
    };

    // 지도 정보 호출
    const getMapInfo = async (type: number) => {
        if (!props.selectedPoiIdx) return;

        if (typeof props.selectedPoiIdx === "string") {
            return;
        }

        const response = await DataProvider.GetPlaceInfo({ mapid, XID: props.selectedPoiIdx.toString(), type: type.toString() });

        if (response.flag) {
            const result = response.result ?? undefined;
            console.log("장소정보호출:",result);
            // 25.12.26 | Kim won jin | 장소 정보 응답 구조 변경 대응
            // 임시로 imageUrl로 올 시 images로 변환 -> 병합 후 삭제
            // if (result.imageUrl) {
            //     const images:ImageItem[] = result.images? result.images : [{iid:0, src: result.imageUrl, isPrimary:true}]; 
            //     result.images=images;
            // }

            console.log("images 추가 후 장소 정보 호출:", result);

            switch (type) {
                case 0:
                    setPrimaryData(response.result);

                    props.setThumbState && props.setThumbState(response.result.thumb);

                    // setTabMenuIdx(TabIndex.MAIN);

                    response.result && changeListItemValue(response.result);
                    break;
                case 1:
                    const fieldArray: Field[] = response.result;
                    const _main: MainDataBundleType = {};

                    fieldArray.forEach((field, idx) => {
                        if (field.value === "${fieldValue}" && field.fieldKey) {
                            const valueIdx = alphabet.indexOf(field.fieldKey);
                            if (valueIdx !== -1) {
                                field.value = mapData?.row2[valueIdx] ?? "";
                            }
                        }
                        _main[`data_${idx}`] = field;
                    });
                    setMainData(result);
                    break;
                default:
                    setDetailData(result);
                    break;
            }
        } else {
            // 지도 데이터가 없는 경우
            if (response.status === 404) {
                navigate("/", { replace: true });
            } else {
                // 데이터 가져오지 못했을 경우
                // mjkang 주요 정보 수신 실패했을 때 처리가 없음
                showModal("장소 정보를 받아올 수 없습니다.");
                closeFSSheet();
            }
        }
    };

    // 도움돼요
    const setThumb = async () => {
        if (props.selectedPoiIdx) {
            if (isLogin) {
                const response = await DataProvider.UpdateUserActivity({ mapid, XID: props.selectedPoiIdx.toString() });

                if (response.flag) {
                    await getMapInfo(0);
                    // props.setThumbState && props.setThumbState(!props.thumbState); // 윗 줄에서 정보를 불러오므로 재설정해주지 않음
                } else {
                    // 로그인 정보가 없는 경우
                    if (response.status && response.status === 403) {
                    }
                }
            } else {
                openMapScreenModal &&
                    openMapScreenModal({
                        title: "안내",
                        children: (
                            <>
                                로그인이 필요한 서비스입니다.
                                <br />
                                로그인을 하시겠습니까?
                            </>
                        ),
                        modalButtons: (
                            <ModalButtons
                                buttonCount={1}
                                buttons={[
                                    {
                                        label: "확인",
                                        color: "Primary",
                                        onClick: () =>
                                            openMapScreenModal &&
                                            openMapScreenModal({
                                                title: "로그인",
                                                children: (
                                                    <LoginForm
                                                        onClose={() => {
                                                            getMapInfo(0);
                                                            closeMapScreenModal && closeMapScreenModal();
                                                        }}
                                                        onClickSignUp={onClickSignUp}
                                                    />
                                                ),
                                                modalButtons: null,
                                                size: "wide",
                                                disableOverlayClose: true,
                                            }),
                                    },
                                ]}
                            />
                        ),
                    });
            }
        }
    };

    // 삭제
    const delAction = async () => {
        // 제작자인지 확인
        if (isCreator && primaryData) {
            const response = await DataProvider.DeletePlaceInfo({ mapid, XID: String(props.selectedPoiIdx) });
            if (response.flag) {
                props.setToast && props.setToast({ type: ToastType.DELETE }); // Toast 호출

                props.setEditPlaceStatus && props.setEditPlaceStatus(true);
                if (props.prevSelectedIdxRef) {
                    props.prevSelectedIdxRef.current = props.selectedPoiIdx as number;
                }
                if (props.onPOIReload) props.onPOIReload(); // 장소목록 리로드
                closeFSSheet();
            } else {
                showModal("장소 삭제에 실패하였습니다.");
            }
        } else {
            if (!isCreator) {
                showModal("제작자가 아닙니다.");
            } else {
                showModal("지도정보가 없습니다.");
            }
        }
    };

    // FloatingSideSheet 닫기 (일반)
    const closeFSSheet = () => {
        props.setShowFloatingSheet && props.setShowFloatingSheet(false); // 플로팅시트 set false
        props.setSelectedPoiIdx && props.setSelectedPoiIdx(0); // 선택된 마커 초기화
        // 마커 하이라이트 해제는 X 버튼 클릭시에만 수행 (자동 닫힘에서는 제외)
    };

    // X 버튼 클릭시 FloatingSideSheet 닫기 (마커 해제 포함)
    const closeFSSheetWithMarkerClear = () => {
        props.onClearSelectedMarker && props.onClearSelectedMarker(); // 마커 하이라이트 해제
        closeFSSheet(); // 기본 닫기 동작
    };

    // 삭제 확인
    const delConfirm = () => {
        setTabMenuIdx(TabIndex.DELETE);
    };

    // 컨텐츠 변경 함수 -----------------------------------------------------------
    const backToMain = () => {
        getMapInfo(0);
        setTabMenuIdx(TabIndex.MAIN);
    };

    const moveToGallery = () => {
        setTabMenuIdx(TabIndex.GALLERY);
    };

    const moveToStory = () => {
        setTabMenuIdx(TabIndex.STORY);
    };

    const editPlace = () => {
        props.changeSideSheet && props.changeSideSheet(ContentList.EditPlace);
        // setTabMenuIdx(TabIndex.EditPlace);
    };

    const moveToStoryGallery = () => {
        setTabMenuIdx(TabIndex.STORYGALLERY);
    };
    // --------------------------------------------------------------------------

    const buttonOptions = {
        [TabIndex.MAIN]: {
            left: isCreator
                ? {
                      label: "삭제",
                      type: props.isAnalysisActive ? MiniButtonType.DISABLED : MiniButtonType.RED,
                      onClick: props.isAnalysisActive ? undefined : delConfirm,
                  }
                : undefined,
            right: isCreator
                ? {
                      label: "수정",
                      type: props.isAnalysisActive ? MiniButtonType.DISABLED : MiniButtonType.BLUE,
                      onClick: props.isAnalysisActive ? undefined : editPlace,
                  }
                : undefined,
        },
        [TabIndex.DETAIL]: {
            right: isCreator
                ? {
                      label: "수정",
                      type: props.isAnalysisActive ? MiniButtonType.DISABLED : MiniButtonType.BLUE,
                      onClick: props.isAnalysisActive ? undefined : editPlace,
                  }
                : undefined,
        },
        [TabIndex.DELETE]: {
            left: { label: "취소", type: MiniButtonType.GRAY, onClick: backToMain },
            right: { label: "삭제", type: MiniButtonType.RED, onClick: delAction },
        },
        [TabIndex.GALLERY]: {
            right: { label: "이전", type: MiniButtonType.GRAY, onClick: backToMain },
        },
        [TabIndex.STORYGALLERY]: {
            right: { label: "이전", type: MiniButtonType.GRAY, onClick: moveToStory },
        },
    };

    const changeFieldValueToData = (s: string, fieldName: string) => {
        if (mapData) {
            let fieldNameIdx = mapData.row1.indexOf(fieldName);
            if (fieldNameIdx != null) {
                return s.replace("${fieldValue}", mapData.row2[fieldNameIdx]);
            }
        }
        return s;
    };

    const makeMainData = () => {
        let mainData: Field[] = [];

        if (mapSettingData) {
            mapSettingData.placeInfo.contents.map(({ template, fieldName, fieldValue, style }) => {
                let val: Field | undefined;

                if (template !== DataTemplate.none) {
                    val = {
                        fieldKey: "",
                        fieldName,
                        value: changeFieldValueToData(fieldValue, fieldName),
                        template: template,
                    };
                } else if (style) {
                    val = {
                        fieldKey: "",
                        fieldName,
                        value: changeFieldValueToData(fieldValue, fieldName),
                        style: {
                            color: style.color,
                            textAlign: style.textAlign,
                            fontStyle: style.fontStyle,
                            fontWeight: style.fontWeight,
                            textDecoration: style.textDecoration,
                        },
                        template: DataTemplate.none,
                    };
                }

                if (val) {
                    mainData.push(val);
                }
            });
        }

        return mainData;
    };

    const handleSelectItem = (e: SyntheticEvent) => {
        let target = e.currentTarget as HTMLDivElement;

        if (isCreateMap) {
            let _mapSettingData = { ...mapSettingData };
            _mapSettingData.placeInfo.selectedId = Number(target.id);
            setMapSettingData(_mapSettingData);
        }
    };

    const handleDrag = (e: DragEvent<HTMLDivElement>, index: number) => {
        const type = e.type;
        const idx = index;

        if (isCreateMap) {
            switch (type) {
                case "dragstart":
                    const dragBox = e.currentTarget;
                    e.dataTransfer.setDragImage(dragBox, 0, 0);
                    dragItem.current = idx;
                    break;
                case "dragenter":
                    dragOverItem.current = idx;
                    const _mapSettingData = { ...mapSettingData };
                    const newList = _mapSettingData.placeInfo.contents;
                    let dragItemValue = newList[dragItem.current as number];

                    newList.splice(dragItem.current as number, 1);
                    newList.splice(dragOverItem.current as number, 0, dragItemValue);
                    _mapSettingData.placeInfo.selectedId = dragOverItem.current as number;
                    dragItem.current = dragOverItem.current;
                    dragOverItem.current = undefined;
                    setMapSettingData(_mapSettingData);

                    break;
                case "dragover":
                    e.preventDefault();
                    break;
                case "dragend":
                    break;
                default:
                    break;
            }
        }
    };

    const handleDeleteItem = (e: SyntheticEvent, idx: number) => {
        e.stopPropagation();

        if (isCreateMap) {
            const _mapSettingData = { ...mapSettingData };
            let newList = _mapSettingData.placeInfo.contents;

            newList.splice(idx, 1);
            _mapSettingData.placeInfo.contents = newList;
            _mapSettingData.placeInfo.selectedId = idx - 1;
            setMapSettingData(_mapSettingData);

            dragItem.current = undefined;
        }
    };

    const handleAddItem = (e: SyntheticEvent) => {
        if (isCreateMap) {
            const _mapSettingData = { ...mapSettingData };
            let newList = _mapSettingData.placeInfo.contents;

            if (newList.length >= 10) return;

            let idx = (newList.length + 1) % mapData.row1.length;
            let content: Content = {
                fieldName: mapData.row1[idx],
                style: {
                    ...defaultStyle,
                },
                template: DataTemplate.none,
                fieldValue: "${fieldValue}",
            };
            newList.push(content);
            _mapSettingData.placeInfo.contents = newList;

            setMapSettingData(_mapSettingData);
            dragItem.current = undefined;
            setAddCheck(!addCheck);
        }
    };

    const [selectedImg, setSelectedImg] = useState<number>(-1);
    const [showImgModal, setShowImgModal] = useState(false);
    const [imageData, setImageData] = useState<ImageOptions[] | undefined>();

    const clickImage = (src: string) => {
        setShowImgModal(true);
        setSelectedImg(0);
        setImageData([{ url: src, IID: 0 }]);
    };

    const onSubmit = async () => {
        if (!formRef.current || !fileUploadRef.current || !mapid) return;
        const formData = new FormData(formRef.current);

        const files = fileUploadRef.current.files;
        if (files && files.length > 0) {
            Array.from(files).forEach((file, index) => {
                formData.append("images", file); // 또는 "files[]", file로 배열처럼 보낼 수도 있음
                formData.append("imagenames", file.name);
            });
        }

        const response = await DataProvider.AddPlaceImage({ mapid, XID: String(props.selectedPoiIdx) }, formData);
        if (!response.flag) {
            switch (response.status) {
                case 403:
                    showModal("장소 수정 권한이 없습니다.");
                    break;
                case 404:
                    showModal("장소를 찾을 수 없습니다.");
                    break;
                default:
                    showModal("장소 수정에 실패하였습니다.");
            }
        } else {
            setPreviewImage(null);
        }
    };

    const onFileUploadClick = () => {
        fileUploadRef.current?.click(); // 파일 선택창 열기
    };

    const onFileAdded = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const file = files[0]; // 첫 번째 파일만 사용한다고 가정
        const previewUrl = URL.createObjectURL(file);
        setPreviewImage(previewUrl);

        onSubmit();

        // onFilesSelected?.(fileArray); // 부모에게 선택된 파일 전달 (필요한 경우)
    };

    useEffect(() => {
        return () => {
            if (previewImage) {
                URL.revokeObjectURL(previewImage);
            }
        };
    }, [previewImage]);

    // 탭 콘텐츠 렌더링 함수 --------------------------------------------------------------------------
    const renderContents = () => {
        // 지도 - 장소정보
        if (primaryData) {
            switch (tabMenuIdx) {
                // 상세정보
                case TabIndex.DETAIL:
                    return detailData ? (
                        <div className={styles.list}>
                            <table className={styles.detail_table}>
                                <colgroup>
                                    <col style={{ width: "25%" }} />
                                    <col style={{ width: "75%" }} />
                                </colgroup>
                                <tbody>
                                    {Object.entries(detailData).map(([key, value]) => {
                                        const strValue = String(value);
                                        const isLink = strValue.startsWith("http") || strValue.startsWith("www"); // 내용이 링크인지 확인
                                        const isImage = /\.(jpg|jpeg|png)$/i.test(strValue); // 내용이 이미지 인지 확인

                                        return (
                                            // <div className={styles.detail_list} key={`subInfo_${key}`}>
                                            //     <span>{key} : </span>
                                            //     <span>{String(value)}</span>
                                            // </div>
                                            <tr key={`subInfo_${key}`}>
                                                <th className={styles.tb_header}>{key}</th>
                                                <td>
                                                    {isImage ? (
                                                        // (<img src={strValue} alt={key} onClick={moveToGallery}/>) :
                                                        <img src={strValue} alt={key} onClick={() => clickImage(strValue)} />
                                                    ) : isLink ? (
                                                        <a href={strValue} target="_blank" rel="noopener noreferrer">
                                                            {" "}
                                                            {strValue}{" "}
                                                        </a>
                                                    ) : (
                                                        strValue
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className={styles.list}> 컨텐츠 없음 </div>
                    );

                // 이야기
                case TabIndex.STORY:
                    return (
                        !props.newPlace && (
                            <StoryView
                                mapId={mapid}
                                xid={Number(props.selectedPoiIdx)}
                                setIsFullView={setIsFullView}
                                isCreator={isCreator}
                                setToast={props.setToast}
                                setStoryImages={setStoryImages}
                                moveToGallery={moveToStoryGallery}
                                openMapScreenModal={openMapScreenModal}
                                closeMapScreenModal={closeMapScreenModal}
                                isAdmin={isAdmin}
                            />
                        )
                    );

                case TabIndex.STORYGALLERY:
                    return (
                        <StoryGridGallery
                            setIsFullView={setIsFullView}
                            xid={Number(props.selectedPoiIdx)}
                            isCreator={isCreator}
                            openMapScreenModal={openMapScreenModal}
                            closeMapScreenModal={closeMapScreenModal}
                            images={storyImages}
                        />
                    );

                case TabIndex.DELETE:
                    return (
                        <>
                            <ContentTitle title={"장소 삭제"} />
                            <div className={styles.del_desc}>
                                <DelConfirm text="장소" />
                            </div>
                        </>
                    );
                case TabIndex.GALLERY:
                    return (
                        props.selectedPoiIdx &&
                        typeof props.selectedPoiIdx === "number" && (
                            <GridGallery
                                setIsFullView={setIsFullView}
                                xid={props.selectedPoiIdx}
                                isCreator={isCreator}
                                openMapScreenModal={openMapScreenModal}
                                closeMapScreenModal={closeMapScreenModal}
                                selectedMapIdx={props.selectedMapIdx ?? 0}
                            />
                        )
                    );

                // 주요정보
                default:
                    return mainData ? (
                        <div className={styles.list}>
                            {props.pageType == "map" ? (
                                <table className={styles.detail_table}>
                                    <colgroup>
                                        <col style={{ width: "25%" }} />
                                        <col style={{ width: "75%" }} />
                                    </colgroup>
                                    {/* <tbody> */}
                                    {Object.entries(mainData).map(([key, value]: any[], index: number) => {
                                        return (
                                            <MainContentItem
                                                key={`mainInfo_${index}`}
                                                data={value}
                                                pageType={props.pageType ? props.pageType : undefined}
                                            />
                                        );
                                    })}
                                    {/* </tbody> */}
                                </table>
                            ) : (
                                <>
                                    {Object.entries(mainData).map(([key, value]: any[], index: number) => {
                                        return (
                                            <MainContentItem
                                                key={`mainInfo_${index}`}
                                                data={value}
                                                pageType={props.pageType ? props.pageType : undefined}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    ) : (
                        <div className={styles.list}> 컨텐츠 없음 </div>
                    );
            }
        } else {
            // 지도만들기 - 미리보기
            if (isCreateMap) {
                const mainData = makeMainData();
                const content = mainData.map((value, idx) => {
                    if (idx === 0) return;

                    return (
                        <div
                            key={idx}
                            id={`${idx}`}
                            className={`${styles.wrapMainContentItem} ${mapSettingData.placeInfo.selectedId === idx ? styles.selected : ""}`}
                            onClick={handleSelectItem}
                            onDragStart={(e) => handleDrag(e, idx)}
                            onDragEnter={(e) => handleDrag(e, idx)}
                            onDragEnd={(e) => handleDrag(e, idx)}
                            onDragOver={(e) => handleDrag(e, idx)}
                        >
                            <button className={`${styles.dragBtn} ${styles.dragIndicator}`} draggable>
                                <Icon name={"dragIndicator"} color={"darkGrey"} />
                            </button>
                            <MainContentItem
                                key={`mainInfo_${idx}`}
                                data={value}
                                isCreate={true}
                                pageType={props.pageType ? props.pageType : undefined}
                            />
                            <button
                                onDragStart={(e) => e.preventDefault()}
                                className={`${styles.deleteBtn} ${styles.del}`}
                                onClick={(e) => handleDeleteItem(e, idx)}
                            >
                                <Icon name={"del"} color={"darkGrey"} />
                            </button>
                        </div>
                    );
                });

                return content;
            }
        }
    };

    return (
        <div className={styles.sheet_container}>
            <section className={`${styles.wrapper} ${isCreateMap ? "" : styles.container_height}`}>
                {!isCreateMap && !props.isAddMode && tabMenuIdx !== TabIndex.GALLERY && <ButtonClose onClick={closeFSSheetWithMarkerClear} />}

                {/* 이미지 영역 */}
                {isCreateMap ? (
                    <div id={"0"} className={`${styles.image_area} ${props.pageType != "map" ? styles.create : ""}`} onClick={handleSelectItem}>
                        {mapSettingData && props.mainImage?.isMainImage ? (
                            <img referrerPolicy="no-referrer" className={styles.main_img} src={props.mainImage.mainimageUrl} alt="대표이미지" />
                        ) : (
                            <div className={styles.default_img}>
                                <Icon name="errorImg" size="xlarge" color="white" />
                                <p> 이미지가 없습니다 </p>
                            </div>
                        )}
                    </div>
                ) : (
                    !isFullView && (
                        <div className={styles.image_area} style={{ cursor: isCreator ? "pointer" : "default" }}>
                            {primaryData && primaryData.images && primaryData.images.length>0 ? (
                                <>
                                    <img className={styles.main_img} src={primaryData.images[0]?primaryData.images[0].src:undefined} alt="대표이미지" onClick={moveToGallery} />
                                    <div className={styles.more}>
                                        {primaryData.images != undefined && (
                                            <MorePictures picturesCount={primaryData.images.length} onClick={moveToGallery} />
                                        )}
                                    </div>
                                </>
                            ) : previewImage ? (
                                <>
                                    <img className={styles.main_img} src={previewImage} alt="업로드된 이미지 미리보기" />
                                </>
                            ) : (
                                <>
                                    <div className={styles.default_img} onClick={isCreator ? onFileUploadClick : undefined}>
                                        <Icon name={props.newPlace ? "errorImg" : "addPhoto2"} size="xlarge" color="white" />
                                        <p>{isCreator ? "클릭하여 이미지 추가" : "이미지 없음"}</p>
                                    </div>
                                    <form ref={formRef} method="POST" encType="multipart/form-data">
                                        <input
                                            ref={fileUploadRef}
                                            type="file"
                                            accept={allowedFileTypes.join(",")}
                                            onChange={onFileAdded}
                                            style={{ display: "none" }}
                                            multiple
                                        />
                                    </form>
                                </>
                            )}
                        </div>
                    )
                )}

                {/* title영역 */}
                <div className={styles.title_area}>
                    {isCreateMap ? (
                        <div className={styles.title}>{mapSettingData.placeInfo.title}</div>
                    ) : (
                        <div className={styles.title}>{primaryData ? primaryData.placeName : "(장소 이름 없음)"}</div>
                    )}

                    {/* 도움돼요 on/off */}
                    {tabMenuIdx !== TabIndex.DELETE && !isCreateMap && !isFullView && !props.newPlace && (
                        <div className={styles.like_it}>
                            {/* 로그인하지 않은 경우 항상 회색 | smyun | 250906 */}
                            <ThumbButton state={isLogin ? props.thumbState! : false} onIconClick={setThumb} />
                        </div>
                    )}
                </div>

                {/* 조회수, 좋아요 갯수 */}
                {tabMenuIdx !== TabIndex.DELETE && !isCreateMap && !props.newPlace && (
                    <div className={styles.desc_count}>
                        {`조회수 ${primaryData ? primaryData.viewCnt : 0}
                    도움돼요 ${primaryData ? primaryData.thumbCnt : 0}`}
                    </div>
                )}
                {/* tab영역 */}
                <div className={`${styles.tab_area} ${tabMenuIdx === TabIndex.STORY ? styles.fixed : ""}`}>
                    {tabMenuIdx < TabIndex.DELETE && (
                        <div className={`${styles.tab_title} ${isCreateMap ? styles.tab_hover : ""}`}>
                            <Tabs
                                options={menu}
                                onItemClick={(idx) => {
                                    // if (idx === 2) return; // 이야기 탭은 기능 미구현으로 비활성화 // 구현 중
                                    setTabMenuIdx(idx);
                                }}
                                tabMenuIdx={tabMenuIdx}
                            />
                        </div>
                    )}
                    <div
                        className={`${styles.tab_desc} ${isCreateMap ? styles.createTabDesc : ""} ${
                            [TabIndex.STORY, TabIndex.GALLERY].includes(tabMenuIdx) ? styles.fixed : ""
                        }`}
                        ref={contentRef}
                    >
                        {renderContents()}
                    </div>
                    {isCreateMap && (
                        // 현재 몇개의 item이 있는지 표시 ex) 4/10
                        // hmchoi item 표시 추가
                        <div className={styles.wrapAddBtn}>
                            <span>
                                {mapSettingData.placeInfo.contents.length}/{MAX_CONTENT_COUNT}
                            </span>
                            <button className={styles.addBtn} onClick={handleAddItem}>
                                <Icon name={"plus"} color={"white"} size={"large"} />
                            </button>
                        </div>
                    )}
                </div>

                {/* 버튼 영역 */}
                {tabMenuIdx !== TabIndex.STORY && !isCreateMap && !props.newPlace && (
                    <div className={styles.button_area}>
                        <BottomButtonBox {...buttonOptions[tabMenuIdx]} isMobileView={props.isMobileView} />
                    </div>
                )}
            </section>
            {/* 접었다펼쳤다 핸들 */}
            {props.shouldFloatingRender && (
                <SideSheetHandle
                    shouldSideRender={props.shouldSideRender ?? false}
                    showSideSheet={props.showSideSheet ?? false}
                    setShowSideSheet={props.setShowSideSheet}
                    showFloatingSheet={props.showFloatingSheet}
                    setShowFloatingSheet={props.setShowFloatingSheet}
                    // 25.07.17 | Kim Young Jun | 사이드 시트 닫힘 상태 추가
                    isClosedSideSheet={props.isClosedSideSheet ?? false}
                    setIsClosedSideSheet={props.setIsClosedSideSheet ?? undefined}
                />
            )}
            {imageData && (
                <ImageModal
                    showModal={showImgModal}
                    setShowModal={setShowImgModal}
                    images={imageData}
                    selected={selectedImg}
                    setSelected={setSelectedImg}
                />
            )}
        </div>
    );
};

export default FloatingSideSheet;
